import { Box, Typography, Card, Stack, Button, Chip, CircularProgress, Alert, Input, IconButton, Tooltip, Table, Sheet, Avatar, useTheme, List, ListItem, ListItemContent, } from "@mui/joy";
import { useContext, useEffect, useState } from "react";
import { LayoutContext } from "../../context";
import { APIGetTests, APIDeleteTest, APIGetTestCategories } from "../../api";
import { useNavigate } from "react-router";
import { useMediaQuery } from "@mui/material";
import { 
  SearchRounded, AddRounded, EditRounded, DeleteRounded, 
  VisibilityRounded, CheckCircleRounded, CancelRounded, 
  FilterListRounded, CategoryRounded, QuestionAnswerRounded,
  CalendarTodayRounded, PersonRounded
} from '@mui/icons-material';


export default function TestsListPage() {
  const { currentDoctor, token } = useContext(LayoutContext);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  // Проверяем права пользователя
  const isAdmin = (currentDoctor?.type_profile.includes('admin') || currentDoctor?.type_profile?.includes('admin')) || (currentDoctor?.type_profile.includes('supervisor') || currentDoctor?.type_profile?.includes('supervisor'));

  // Загрузка тестов
  const loadTests = async (params = {}) => {
    try {
      setLoading(true);
      setError(null);
      
      // Если пользователь не админ, фильтруем по создателю
      if (!isAdmin && currentDoctor?.id) {
        params.created_by = currentDoctor.id;
      }
      
      // Если есть поисковый запрос
      if (searchTerm) {
        params.search = searchTerm;
      }
      
      // Фильтр по статусу активности
      params.is_active = true; // показываем только активные тесты
      
      const response = await APIGetTests(token, params);
      
      if (response.success) {
        setTests(response.tests || []);
      } else {
        setError(response.error || 'Ошибка при загрузке тестов');
      }
    } catch (error) {
      console.error("Ошибка при загрузке тестов:", error);
      setError("Ошибка при загрузке тестов");
    } finally {
      setLoading(false);
    }
  };

  // Добавим функцию удаления в компонент
  const handleDeleteTest = async (testId, testName) => {
    if (!window.confirm(`Вы уверены, что хотите удалить тест "${testName}"?\nЭто действие удалит все связанные вопросы, ответы и результаты.`)) {
      return;
    }

    try {
      setDeleting(true);
      const response = await APIDeleteTest(token, testId);
      
      if (response.success) {
        // Удаляем тест из списка
        setTests(tests.filter(test => test.id !== testId));
        alert('Тест успешно удален');
      } else {
        setError(response.error || 'Ошибка при удалении теста');
      }
    } catch (error) {
      console.error("Ошибка при удалении теста:", error);
      setError("Ошибка при удалении теста");
    } finally {
      setDeleting(false);
    }
  };

  // Форматирование даты
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('ru-RU');
  };

  // Получение цвета статуса
  const getStatusColor = (isActive) => {
    return isActive ? "success" : "danger";
  };

  useEffect(() => {
    loadTests();
  }, []);

  // Десктопная версия таблицы
  const DesktopTable = () => (
    <Sheet sx={{ height: 'auto', overflow: "hidden" }}>
      <Table
        aria-label="Таблица тестов"
        stripe="odd"
        hoverRow
        sx={{
          '& thead th': { fontWeight: 'lg' },
          '& tbody tr:hover': { backgroundColor: 'background.level1' },
        }}
      >
        <thead>
          <tr>
            <th style={{ width: 150 }}>Название</th>
            <th>Категория</th>
            <th>Вопросы</th>
            <th>Тип</th>
            <th>Статус</th>
            <th>Дата создания</th>
            <th style={{ textAlign: 'center' }}>Действия</th>
          </tr>
        </thead>
        <tbody>
          {tests.map((test) => (
            <tr key={test.id}>
              <td>
                <Typography level="body-sm" fontWeight="lg">
                  {test.name}
                </Typography>
                {test.description && (
                  <Typography level="body-xs" color="neutral">
                    {test.description.substring(0, 50)}...
                  </Typography>
                )}
              </td>
              <td>
                {test.categories && test.categories.length > 0 ? (
                  <Stack direction="row" spacing={0.5} flexWrap="wrap">
                    {test.categories.slice(0, 2).map((category) => (
                      <Chip key={category.id} size="sm" variant="soft" color="primary">
                        {category.name}
                      </Chip>
                    ))}
                    {test.categories.length > 2 && (
                      <Chip size="sm" variant="outlined">
                        +{test.categories.length - 2}
                      </Chip>
                    )}
                  </Stack>
                ) : (
                  <Typography level="body-xs" color="neutral">
                    Без категорий
                  </Typography>
                )}
              </td>
              <td>
                <Chip size="sm" variant="outlined">
                  {test.questions_count || 0} вопросов
                </Chip>
              </td>
              <td>
                <Chip size="sm" variant="outlined" color={test.test_type === 'psychology' ? 'primary' : 'neutral'}>
                  {test.test_type === 'psychology' ? 'Психологический' :
                  test.test_type === 'diagnostic' ? 'Диагностический' :
                  test.test_type === 'screening' ? 'Скрининговый' : 'Самооценка'}
                </Chip>
              </td>
              <td>
                <Chip
                  size="sm"
                  variant="soft"
                  color={getStatusColor(test.is_active)}
                  startDecorator={test.is_active ? <CheckCircleRounded /> : <CancelRounded />}
                >
                  {test.is_active ? 'Активен' : 'Неактивен'}
                </Chip>
              </td>
              <td>
                <Typography level="body-sm">
                  {formatDate(test.date_create)}
                </Typography>
              </td>
              <td>
                <Stack direction="row" spacing={1} justifyContent="center">
                  {/* <Tooltip title="Просмотреть">
                    <IconButton
                      size="sm"
                      variant="outlined"
                      color="primary"
                      onClick={() => navigate(`/tests/${test.id}`)}
                    >
                      <VisibilityRounded />
                    </IconButton>
                  </Tooltip> */}
                  <Tooltip title="Редактировать">
                    <IconButton
                      size="sm"
                      variant="outlined"
                      color="neutral"
                      onClick={() => navigate(`/tests/${test.id}/edit`)}
                    >
                      <EditRounded />
                    </IconButton>
                  </Tooltip>
                  {isAdmin && (
                    <Tooltip title="Удалить">
                      <IconButton
                        size="sm"
                        variant="outlined"
                        color="danger"
                        loading={deleting && deletingId === test.id}
                        onClick={() => handleDeleteTest(test.id, test.name)}
                      >
                        <DeleteRounded />
                      </IconButton>
                    </Tooltip>
                  )}
                </Stack>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </Sheet>
  );

  // Мобильная версия списка
  const MobileList = () => (
    <List
      sx={{
        '--ListItem-paddingY': '1rem',
        '--ListItem-paddingX': '1rem',
        '--ListItem-borderRadius': '8px',
        '--ListItem-gap': '1rem',
      }}
    >
      {tests.map((test) => (
        <Card
          key={test.id}
          variant="outlined"
          sx={{
            mb: 2,
            p: 0,
            '&:hover': {
              boxShadow: 'md',
              borderColor: 'neutral.outlinedHoverBorder',
            },
          }}
        >
          <ListItem>
            <ListItemContent>
              <Stack spacing={2}>
                {/* Заголовок и описание */}
                <Box>
                  <Typography level="title-lg">{test.name}</Typography>
                  {test.description && (
                    <Typography level="body-sm" color="neutral" sx={{ mt: 0.5 }}>
                      {test.description.length > 100 
                        ? `${test.description.substring(0, 100)}...` 
                        : test.description}
                    </Typography>
                  )}
                </Box>

                {/* Информационные чипы */}
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {/* Категории */}
                  {test.categories && test.categories.length > 0 ? (
                    <Chip size="sm" variant="soft" color="primary">
                      {test.categories[0].name}
                      {test.categories.length > 1 && ` +${test.categories.length - 1}`}
                    </Chip>
                  ) : (
                    <Chip size="sm" variant="outlined">Без категорий</Chip>
                  )}

                  {/* Количество вопросов */}
                  <Chip 
                    size="sm" 
                    variant="outlined" 
                    startDecorator={<QuestionAnswerRounded />}
                  >
                    {test.questions_count || 0}
                  </Chip>

                  {/* Тип теста */}
                  <Chip 
                    size="sm" 
                    variant="outlined" 
                    color={test.test_type === 'psychology' ? 'primary' : 'neutral'}
                  >
                    {test.test_type === 'psychology' ? 'Псих.' :
                     test.test_type === 'diagnostic' ? 'Диаг.' :
                     test.test_type === 'screening' ? 'Скрин.' : 'Сам.'}
                  </Chip>

                  {/* Статус */}
                  <Chip
                    size="sm"
                    variant="soft"
                    color={getStatusColor(test.is_active)}
                    startDecorator={test.is_active ? <CheckCircleRounded /> : <CancelRounded />}
                  >
                    {test.is_active ? 'Активен' : 'Неактивен'}
                  </Chip>
                </Stack>

                {/* Дополнительная информация */}
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CalendarTodayRounded sx={{ fontSize: '0.875rem', color: 'neutral.500' }} />
                    <Typography level="body-xs" color="neutral">
                      {formatDate(test.date_create)}
                    </Typography>
                  </Stack>

                  {/* Кнопки действий */}
                  <Stack direction="row" spacing={1}>
                    {/* <Tooltip title="Просмотреть">
                      <IconButton
                        size="sm"
                        variant="outlined"
                        color="primary"
                        onClick={() => navigate(`/tests/${test.id}`)}
                      >
                        <VisibilityRounded />
                      </IconButton>
                    </Tooltip> */}
                    <Tooltip title="Редактировать">
                      <IconButton
                        size="sm"
                        variant="outlined"
                        color="neutral"
                        onClick={() => navigate(`/tests/${test.id}/edit`)}
                      >
                        <EditRounded />
                      </IconButton>
                    </Tooltip>
                    {isAdmin && (
                      <Tooltip title="Удалить">
                        <IconButton
                          size="sm"
                          variant="outlined"
                          color="danger"
                          loading={deleting && deletingId === test.id}
                          onClick={() => handleDeleteTest(test.id, test.name)}
                        >
                          <DeleteRounded />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Stack>
                </Stack>
              </Stack>
            </ListItemContent>
          </ListItem>
        </Card>
      ))}
    </List>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <Box
        component="main"
        className="MainContent"
        sx={{
          px: { xs: 1, sm: 2, md: 6 },
          pt: { xs: 1, sm: 2 },
          pb: { xs: 1, sm: 2, md: 3 },
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0,
          gap: 2,
        }}
      >
        {/* Заголовок и кнопки */}
        <Box
          sx={{
            display: 'flex',
            mb: 1,
            gap: 1,
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'start', sm: 'center' },
            flexWrap: 'wrap',
            justifyContent: 'space-between',
          }}
        >
          <Typography level="h2" component="h1" sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }} startDecorator={<CategoryRounded />}>
            Тесты и опросники
          </Typography>
          
          <Stack direction="row" spacing={1} sx={{ width: { xs: '100%', sm: 'auto' } }}>
            <Button
              startDecorator={<AddRounded />}
              onClick={() => navigate('/tests/create')}
              fullWidth={isMobile}
              size={isMobile ? 'sm' : 'md'}
            >
              Создать тест
            </Button>
          </Stack>
        </Box>

        {/* Сообщения об ошибках */}
        {error && (
          <Alert color="danger" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Поиск */}
        <Input
          size={isMobile ? 'sm' : 'md'}
          placeholder="Поиск по названию теста..."
          startDecorator={<SearchRounded />}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && loadTests({ search: searchTerm })}
          endDecorator={
            <Button 
              size={isMobile ? 'sm' : 'md'} 
              onClick={() => loadTests({ search: searchTerm })}
            >
              Найти
            </Button>
          }
          sx={{
            '& .MuiInput-endDecorator': {
              ml: 1
            }
          }}
        />

        {/* Контент */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : tests.length > 0 ? (
          <>
            {/* Показываем количество тестов на мобильных */}
            {isMobile && (
              <Typography level="body-sm" color="neutral" sx={{ mb: 0 }}>
                Найдено тестов: {tests.length}
              </Typography>
            )}
            
            {isMobile ? <MobileList /> : <DesktopTable />}
          </>
        ) : (
          <Alert color="neutral" sx={{ mt: 2 }}>
            Тесты не найдены
          </Alert>
        )}
      </Box>
    </Box>
  );
}